import { Injectable } from '@angular/core';
import { MainService } from './main.service';
import { localStorageService } from './localstorage.service';

/**
 * One row of `recuvery_static.multi_countries`, as delivered by
 * `auth/get_settings` -> `country_data[]`.
 */
export interface phoneCountryType {
  country_id: number;
  country_name?: string;
  country_code?: string;
  /** Calling code, stored WITH the '+', e.g. '+91'. */
  mobile_code?: string;
  /** Digit mask, '0' is the placeholder, e.g. '00000 00000'. */
  country_phone_format?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PhoneFormatService {
  constructor(private _main: MainService, private _local: localStorageService) {}

  /**
   * Country row for a country_id, falling back to the country the panel is
   * currently showing. Country data comes from `auth/get_settings`.
   */
  getCountry(country_id?: any): phoneCountryType | null {
    const country_data = this._main.country_data$.getValue() || [];
    const id = country_id || this._local.customGetItem('country_id');
    return country_data.find((country: phoneCountryType) => Number(country.country_id) === Number(id)) || null;
  }

  /**
   * Formats a number using its country's `country_phone_format` mask.
   *
   * Display only: it injects '(', ')', spaces and '-', so never bind it to a
   * form control and never send the result to the API.
   */
  format(value: any, country_id?: any): string {
    const raw = value === null || value === undefined ? '' : String(value).trim();
    if (!raw) {
      return '';
    }

    let digits = raw.replace(/\D/g, '');
    if (!digits) {
      return raw; // 'N/A', '-', and other non-numeric placeholders
    }

    const country = this.getCountry(country_id);
    const mobile_code = String(country?.mobile_code || '').trim().replace(/[-\s]+$/, '');
    const mask = country?.country_phone_format || '';
    const slots = (mask.match(/[0#]/g) || []).length;
    if (!slots) {
      return raw; // no usable country config
    }

    // Drop the calling code when the number already carries it. A written '+'
    // is unambiguous; bare digits are only trimmed when the length then fits,
    // so a US number starting with '1' keeps its first digit.
    const code_digits = mobile_code.replace(/\D/g, '');
    if (code_digits && digits.startsWith(code_digits) &&
      (raw.startsWith('+') || digits.length === code_digits.length + slots)) {
      digits = digits.slice(code_digits.length);
    }

    if (digits.length !== slots) {
      // Show it plainly rather than forcing a wrong-length number into the mask,
      // which would silently misrepresent the data.
      return mobile_code ? `${mobile_code} ${digits}` : digits;
    }

    let i = 0;
    const formatted = mask.replace(/[0#]/g, () => digits.charAt(i++));
    return mobile_code ? `${mobile_code} ${formatted}` : formatted;
  }
}
